import { ReactNode } from 'react';

import { QueryErrorResetBoundary as ReactQueryErrorResetBoundary } from '@mint-lib/query-client';

import { ErrorBoundary } from './ErrorBoundary.jsx';

/**
 * QueryErrorResetBoundary is a React Component that resets the error state of a React Query when the user navigates away from a page or component.
 * It is useful for when a query fails and you want to reset its error state automatically when the user navigates to a different page or component.
 * It also sends these errors to Datadog Real User Monitoring (RUM) for further analysis.
 *
 * @component
 *
 * @param children - The child components that the QueryErrorResetBoundary will encapsulate.
 * @param scope - The scope in which the QueryErrorResetBoundary is used, default is 'react-query-error-boundary'. This is used to categorize the errors in Datadog.
 *
 * @example
 * <QueryErrorResetBoundary scope="homepage">
 *   <Homepage />
 * </QueryErrorResetBoundary>
 */

export type QueryErrorResetBoundaryProps = {
  children: ReactNode;
  scope?: string;
  fallback?: ReactNode;
  type?: 'component' | 'page';
  hasForbiddenOn403?: boolean;
};

export const QueryErrorResetBoundary = ({
  children,
  fallback,
  scope = 'react-query-error-boundary',
  type,
  hasForbiddenOn403,
}: QueryErrorResetBoundaryProps) => (
  <ReactQueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        scope={scope}
        onReset={reset}
        fallback={fallback}
        type={type}
        hasForbiddenOn403={hasForbiddenOn403}
      >
        {children}
      </ErrorBoundary>
    )}
  </ReactQueryErrorResetBoundary>
);
